import React, { useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

export interface FilterOption {
  label: string;
  value: any;
}

export interface ChipFilterConfig {
  id: string;
  label: string;
  options: FilterOption[];
}

export interface MultiChipFilterConfig {
  id: string;
  label: string;
  options: FilterOption[];
}

export interface SearchableMultiChipFilterConfig {
  id: string;
  label: string;
  placeholder?: string;
  onSearch: (query: string) => Promise<FilterOption[]>;
}

export interface SearchFilterConfig {
  id: string;
  label: string;
  placeholder: string;
  onSearch: (query: string) => void;
}

interface FilterDrawerProps {
  open: boolean;
  onClose: () => void;
  filters: Record<string, any>;
  onFilterChange: (field: string, value: any) => void;
  onClearFilters: () => void;
  chipFilters?: ChipFilterConfig[];
  multiChipFilters?: MultiChipFilterConfig[];
  searchableMultiChipFilters?: SearchableMultiChipFilterConfig[];
  searchFilters?: SearchFilterConfig[];
}

export const FilterDrawer: React.FC<FilterDrawerProps> = ({
  open,
  onClose,
  filters,
  onFilterChange,
  onClearFilters,
  chipFilters = [],
  multiChipFilters = [],
  searchableMultiChipFilters = [],
  searchFilters = [],
}) => {
  const [searchInputs, setSearchInputs] = useState<Record<string, string>>({});
  const [searchResults, setSearchResults] = useState<Record<string, FilterOption[]>>({});
  const searchTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const handleSearchableInput = (
    groupId: string,
    query: string,
    onSearch: (q: string) => Promise<FilterOption[]>
  ) => {
    setSearchInputs((prev) => ({ ...prev, [groupId]: query }));
    clearTimeout(searchTimers.current[groupId]);

    if (query.trim().length < 2) {
      setSearchResults((prev) => ({ ...prev, [groupId]: [] }));
      return;
    }

    searchTimers.current[groupId] = setTimeout(async () => {
      const results = await onSearch(query.trim());
      setSearchResults((prev) => ({ ...prev, [groupId]: results }));
    }, 300);
  };

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    filterConfig: SearchFilterConfig
  ) => {
    onFilterChange(filterConfig.id, event.target.value);
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 350, p: 3, fontFamily: "Roboto, sans-serif" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: "bold" }}>
            Filtros
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Stack spacing={4}>
          {/* Single-select chip filters */}
          {chipFilters.map((group) => (
            <Stack key={group.id} spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {group.label}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {group.options.map((option) => (
                  <Chip
                    key={option.value}
                    label={option.label}
                    clickable
                    color={filters[group.id] === option.value ? "primary" : "default"}
                    onClick={() => onFilterChange(group.id, option.value)}
                  />
                ))}
              </Stack>
            </Stack>
          ))}

          {/* Multi-select chip filters (static options) */}
          {multiChipFilters.map((group) => {
            const selectedValues: any[] = filters[group.id] ?? [];
            return (
              <Stack key={group.id} spacing={1}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {group.label}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {group.options.map((option) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <Chip
                        key={option.value}
                        label={option.label}
                        clickable
                        color={isSelected ? "primary" : "default"}
                        onClick={() => {
                          const next = isSelected
                            ? selectedValues.filter((v) => v !== option.value)
                            : [...selectedValues, option.value];
                          onFilterChange(group.id, next);
                        }}
                      />
                    );
                  })}
                </Stack>
              </Stack>
            );
          })}

          {/* Searchable multi-select chip filters */}
          {searchableMultiChipFilters.map((group) => {
            const selectedOptions: FilterOption[] = filters[group.id] ?? [];
            const selectedValues = selectedOptions.map((o) => o.value);
            const inputValue = searchInputs[group.id] ?? "";
            const results = (searchResults[group.id] ?? []).filter(
              (r) => !selectedValues.includes(r.value)
            );

            return (
              <Stack key={group.id} spacing={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {group.label}
                </Typography>

                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder={group.placeholder ?? "Buscar..."}
                  value={inputValue}
                  onChange={(e) =>
                    handleSearchableInput(group.id, e.target.value, group.onSearch)
                  }
                />

                {results.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {results.map((option) => (
                      <Chip
                        key={option.value}
                        label={option.label}
                        size="small"
                        clickable
                        onClick={() =>
                          onFilterChange(group.id, [...selectedOptions, option])
                        }
                      />
                    ))}
                  </Stack>
                )}

                {selectedOptions.length > 0 && (
                  <Stack spacing={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      Selecionadas
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {selectedOptions.map((option) => (
                        <Chip
                          key={option.value}
                          label={option.label}
                          size="small"
                          color="primary"
                          onDelete={() =>
                            onFilterChange(
                              group.id,
                              selectedOptions.filter((o) => o.value !== option.value)
                            )
                          }
                        />
                      ))}
                    </Stack>
                  </Stack>
                )}
              </Stack>
            );
          })}

          {/* Search filters */}
          {searchFilters.map((searchConfig) => {
            const currentValue = filters[searchConfig.id] || "";
            return (
              <Stack key={searchConfig.id} spacing={1.25}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                  {searchConfig.label}
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder={searchConfig.placeholder}
                  value={currentValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleSearchChange(e, searchConfig)
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter") searchConfig.onSearch(currentValue);
                  }}
                />
                <Button
                  fullWidth
                  variant="contained"
                  onClick={() => {
                    searchConfig.onSearch(currentValue);
                    onClose();
                  }}
                  sx={{ textTransform: "none", alignSelf: "flex-start" }}
                >
                  Buscar
                </Button>
              </Stack>
            );
          })}

          {/* Clear filters */}
          <Button
            fullWidth
            variant="outlined"
            startIcon={<CloseIcon />}
            onClick={onClearFilters}
            sx={{ mt: 1, textTransform: "none", fontWeight: 500 }}
          >
            Limpar Filtros
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
};
